import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BankBranch, downloadBlob } from 'receipts';
import { debounceTime, filter, switchMap, tap } from 'rxjs';
import { NewCustomerService } from '../../../services/new-customer.service';
import { OrderProposalService } from '../../../services/order-proposal.service';
import { digitsOnlyControl, isValidSdi, sdiFormatValidator, upperCaseControl } from '../../../utils/new-customer.utils';
import { NewCustomerPreparing } from '../new-customer-preparing/new-customer-preparing';
import { NewCustomerUpload } from '../new-customer-upload/new-customer-upload';

type BankBranchOption = BankBranch & { descrizione: string | null };

// Dati bancari: la condizione scelta chiede la banca o il contatto per la consegna; il modulo si può mandare anche dopo
@Component({
  selector: 'ord-new-customer-bank',
  imports: [
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NewCustomerPreparing,
    NewCustomerUpload,
    ReactiveFormsModule,
  ],
  templateUrl: './new-customer-bank.html',
  styleUrl: './new-customer-bank.scss',
})
export class NewCustomerBank implements OnInit {
  readonly flow = inject(NewCustomerService);
  private readonly proposalService = inject(OrderProposalService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  readonly payment = this.flow.payment;
  readonly type = computed(() => this.payment().condizione?.type ?? null);

  readonly phoneControl = new FormControl('', { nonNullable: true });
  readonly sdiControl = new FormControl('', { nonNullable: true, validators: [sdiFormatValidator] });
  // La scelta arriva dall'autocomplete come oggetto: si cerca solo sul testo scritto
  readonly abiControl = new FormControl<string | BankBranch>('', { nonNullable: true });
  readonly cabControl = new FormControl<string | BankBranchOption>('', { nonNullable: true });
  readonly abiOptions = signal<BankBranch[]>([]);
  readonly cabOptions = signal<BankBranchOption[]>([]);

  // Senza modulo si avvisa una volta: il modulo si può mandare anche dopo
  readonly moduleWarning = signal(false);

  readonly valid = computed(() => {
    const payment = this.payment();
    return !!payment.condizione
      && (payment.condizione.type !== 'ABICAB' || !!payment.cab)
      && (payment.condizione.type !== 'CONTATTO' || !!payment.contatto)
      && (!payment.codiceSdi || isValidSdi(payment.codiceSdi));
  });

  // Nel campo l'ABI prima della banca
  readonly displayBank = (bank: BankBranch | string | null): string =>
    typeof bank === 'string' ? bank : bank ? `${bank.abi} - ${bank.banca}` : '';
  readonly displayBranch = (branch: BankBranchOption | string | null): string =>
    typeof branch === 'string' ? branch : branch?.descrizione ?? branch?.cab ?? '';

  ngOnInit(): void {
    const payment = this.payment();
    this.phoneControl.setValue(payment.contatto ?? '', { emitEvent: false });
    this.sdiControl.setValue(payment.codiceSdi ?? '', { emitEvent: false });
    this.abiControl.setValue(payment.abi ? `${payment.abi} - ${payment.banca ?? ''}` : '', { emitEvent: false });
    this.cabControl.setValue(payment.agenzia ?? payment.cab ?? '', { emitEvent: false });

    digitsOnlyControl(this.phoneControl, this.destroyRef);
    upperCaseControl(this.sdiControl, this.destroyRef);

    this.phoneControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.flow.setPayment({ ...this.payment(), contatto: value.trim() || null }));

    this.sdiControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.flow.setPayment({ ...this.payment(), codiceSdi: value.trim() || null }));

    // ABI: banche dal testo scritto; il testo cancellato toglie anche l'agenzia
    this.abiControl.valueChanges
      .pipe(
        filter((value): value is string => typeof value === 'string'),
        tap(value => {
          if (!value.trim()) {
            this.flow.setPayment({ ...this.payment(), abi: null, banca: null, cab: null, agenzia: null });
            this.cabControl.setValue('', { emitEvent: false });
            this.cabOptions.set([]);
          }
        }),
        debounceTime(300),
        switchMap(text => this.proposalService.searchBanks(text)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(banks => this.abiOptions.set(banks));

    // CAB: agenzie della banca scelta filtrate da quanto scritto, senza banca solo sul CAB scritto
    this.cabControl.valueChanges
      .pipe(
        filter((value): value is string => typeof value === 'string'),
        tap(() => this.flow.setPayment({ ...this.payment(), cab: null, agenzia: null })),
        debounceTime(300),
        switchMap(text => this.proposalService.searchCabs(this.payment().abi, text)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(branches => this.cabOptions.set(branches));

    if (payment.abi) {
      this.loadCabs(payment.abi);
    }
  }

  selectPayment(value: string): void {
    const condizione = this.flow.payments().find(option => option.value === value) ?? null;
    this.flow.setPayment({ ...this.payment(), condizione });
  }

  // Un'altra banca toglie l'agenzia scelta: i suoi CAB sono già pronti all'apertura
  selectAbi(bank: BankBranch): void {
    this.flow.setPayment({ ...this.payment(), abi: bank.abi, banca: bank.banca, cab: null, agenzia: null });
    this.cabControl.setValue('', { emitEvent: false });
    this.loadCabs(bank.abi);
  }

  // Agenzia scelta: ABI, CAB e banca insieme
  selectCab(branch: BankBranchOption): void {
    this.flow.setPayment({ ...this.payment(), abi: branch.abi, banca: branch.banca, cab: branch.cab, agenzia: branch.descrizione });
    this.abiControl.setValue(`${branch.abi} - ${branch.banca}`, { emitEvent: false });
  }

  setVatExempt(esenzioneIva: boolean): void {
    this.flow.setPayment({ ...this.payment(), esenzioneIva });
  }

  downloadTemplate(): void {
    if (this.flow.downloading()) {
      return;
    }

    this.flow.downloadBankTemplate().subscribe({
      next: ({ blob, fileName }) => {
        downloadBlob(blob, fileName);
        this.snackBar.open('Template dati bancari scaricato con successo.', 'Chiudi', { duration: 5000 });
      },
      error: (error: unknown) => {
        const message = this.flow.errorMessage(error);
        if (message) {
          this.snackBar.open(message, 'Chiudi', { duration: 5000 });
        }
      },
    });
  }

  continue(): void {
    if (!this.valid()) {
      return;
    }

    if (this.flow.bankDocuments().length === 0 && !this.moduleWarning()) {
      this.moduleWarning.set(true);
      return;
    }

    this.flow.go('note');
  }

  private loadCabs(abi: string): void {
    this.proposalService.searchCabs(abi, '').subscribe(branches => this.cabOptions.set(branches));
  }
}
